import { LitElement, html, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import tailwindStyles from "../../index.css?inline";

interface ElementProps {
  path: string;
  name: string;
  switchName: string;
}

@customElement("element-loader")
export class ElementLoader extends LitElement {
  @property({ type: Object }) pl: any;

  @state() private _element: any = null;
  @state() private loading = false;
  @state() private el: any = null;

  private renderInstance = 0;


  @property({ type: Object })
  set element(val: any) {
    let oldVal = this._element;
    this._element = val;

    if (oldVal !== val) {
      this.load();
    }
  }

    static styles = [unsafeCSS(tailwindStyles)]
  

  get element() {
    return this._element;
  }

  get headless(): boolean {
    return false;
  }

  /**
   * Used to access the active loaded element instance
   */
  loadedElement() {
    return this.el;
  }

  elementPath(el?: any): ElementProps {
    if (!el) el = this._element;

    let switchName = el.variableType.toLocaleLowerCase();
    // if element variable type is 1 worded, warn and append element
    if (switchName.split(" ").length === 1) {
      // backward compatibility for single element names
      switchName += " element";
    }

    // replace spaces with dashes
    let eln = switchName.replace(/ /g, "-");
    let elp = `${eln}/${eln}.js`;

    return {
      path: elp,
      name: eln,
      switchName: el.variableType,
    };
  }

  checkTheme(theme: string, eln: string): string {
    if (theme === "bulkit") {
      return `${eln}.js`;
    } else {
      return `${eln}/${eln}.js`;
    }
  }

  load() {
    let el = this._element;
    // render count
    // this is incremented on each _element update
    // it is used to cancel out outdated callbacks
    this.renderInstance += 1;

    const elementProps = this.elementPath();

    // Display loading indicator
    if (!this.headless) this.loading = true;

    this.loadDynamic(
      elementProps,
      this.renderInstance,
      (newElement: any, rI: number) => {
        // check if callback is outdated,
        // only the highest renderInstance is valid
        if (rI < this.renderInstance) {
          return;
        } else {
          try {
            this.el = newElement;

            // Transfer all methods from pl to the new element
            this.transferAllMethods(newElement);

            if (typeof newElement.init === "function") {
              newElement.init(el, this);
              this.replaceWith(newElement);
            } else {
              throw new Error("newElement.init is not a function");
            }
          } catch (e: any) {
            if (e.message === "newElement.init is not a function") {
              console.error(
                `Custom Element Doesn't implement init: ${elementProps.path}`
              );
            } else {
              console.error("Error initializing element:", e);
            }
            this.handleLoadError(elementProps.name);
          }
        }
      }
    );
  }

  /**
   * Transfer all methods from pl to the target element
   */
  private transferAllMethods(targetElement: any) {
    if (!this.pl || !targetElement) {
      console.warn("Cannot transfer methods: pl or targetElement is undefined");
      return;
    }

    // Transfer all function properties from pl to the target element
    Object.getOwnPropertyNames(Object.getPrototypeOf(this.pl))
      .concat(Object.keys(this.pl))
      .forEach((methodName) => {
        // Skip constructor and avoid transferring multiple times
        if (
          methodName !== "constructor" &&
          typeof this.pl[methodName] === "function" &&
          !targetElement[methodName]
        ) {
          targetElement[methodName] = (...args: any[]) => {
            return this.pl[methodName].apply(this.pl, args);
          };
        }
      });
  }

  /**
   * Dynamically imports a module from elementPath,
   * creates a new node of elementName then passes it to a callback function cb
   */
  async importAndInit(
    elementImport: Promise<any>,
    elementName: string,
    renderInstance: number,
    cb: (element: any, instance: number) => void
  ) {
    try {
      await elementImport;

      if (!this.headless) {
        let newElement = document.createElement(elementName);

        // Transfer all methods to the new element
        this.transferAllMethods(newElement);

        // Check if custom element is properly defined
        if (newElement.constructor === HTMLElement) {
          throw new DOMException(
            `Custom Element Not Found: ${elementName}. Make sure it's properly defined.`
          );
        }
        if (cb) cb(newElement, renderInstance);
      }
    } catch (error: any) {
      console.error(`Failed to import ${elementName}:`, error);
      this.handleImportError(elementName, renderInstance, cb);
    }
  }

  private async handleImportError(
    elementName: string,
    renderInstance: number,
    _cb: (element: any, instance: number) => void
  ) {
    const missingElementName = "missing-element";

    // avoid recursive loop, return if it's missing-element
    if (elementName === missingElementName) {
      console.error("Recursive error: missing-element itself failed to load");
      return;
    }

    try {
      const missingElementImport = import("./missing-element");
      await this.importAndInit(
        missingElementImport,
        missingElementName,
        renderInstance,
        (newElement: any) => {
          if (!this.headless) {
            // Transfer methods to missing element as well
            this.transferAllMethods(newElement);
            (newElement as any).msg = elementName;
            this.replaceWith(newElement);
          }
        }
      );
    } catch (fallbackError) {
      console.error("Even missing-element failed to load:", fallbackError);
      this.loading = false;
      this.requestUpdate();
    }
  }

  private handleLoadError(elementName: string) {
    console.error(`Completely failed to load element: ${elementName}`);
    this.loading = false;
    this.requestUpdate();
  }

async replaceWith(newElement: HTMLElement) {
  this.loading = false;
  
  // Wait for component update
  await this.updateComplete;
  
  // Clear existing content
  if (this.shadowRoot) {
    const content = this.shadowRoot.querySelector('#content');
    if (content) {
      content.innerHTML = '';
      content.appendChild(newElement);
    } else {
      // Create content container if it doesn't exist
      const container = document.createElement('div');
      container.id = 'content';
      container.className = 'w-full h-full';
      container.appendChild(newElement);
      this.shadowRoot.appendChild(container);
    }
  }
  
  this.requestUpdate();
}

  /**
   * Checks element module existence in auto-load configs
   * then triggers the loading from the right location
   */
  loadDynamic(
    elementProps: ElementProps,
    rI: number,
    cb: (element: any, instance: number) => void
  ) {
    let importPath;

    if (
      window.location.hostname === "localhost" ||
      window.location.hostname.includes("127.0.0.1")
    ) {
      // Development
      importPath = `../dsv4.0/elements/${elementProps.path}`;
    } else {
      // Production - elements should be in the same directory as other assets
      importPath = `./elements/${elementProps.path}`;
    }

    const elementImport = import(/* webpackIgnore: true */ importPath).catch(
      (error) => {
        console.error(`Dynamic import failed for: ${importPath}`, error);
        throw error;
      }
    );

    this.importAndInit(elementImport, elementProps.name, rI, cb);
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex justify-center items-center w-full py-8">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
          ></div>
          <span class="ml-3 text-gray-600 text-sm">Loading element...</span>
        </div>
      `;
    } else {
      return html`<div id="content" class="w-full h-full"></div>`;
    }
  }
}
